import { useEffect, useRef, useState } from 'react';
import { Icon, type IconName } from './Icon';
import { useDialogFocus } from './useDialogFocus';
import { useT } from '../i18n/LangProvider';

export interface Destination {
  id: string;
  label: string;
  crumb: string;
  icon: IconName;
}

/** Case-insensitive substring match over label + breadcrumb. Pure — unit-tested. */
export function filterDestinations(items: Destination[], query: string): Destination[] {
  const q = query.trim().toLowerCase();
  if (!q) return items;
  return items.filter((d) => `${d.label} ${d.crumb}`.toLowerCase().includes(q));
}

/**
 * The ⌘K / topbar-search command palette. Type to filter the app's sections and
 * hit Enter (or click) to jump there — the search box in the topbar is no longer
 * decorative.
 */
export function CommandPalette({
  open,
  destinations,
  onClose,
  onSelect,
}: {
  open: boolean;
  destinations: Destination[];
  onClose: () => void;
  onSelect: (id: string) => void;
}) {
  const t = useT();
  const [query, setQuery] = useState('');
  const [index, setIndex] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  useDialogFocus(ref, open, onClose);
  useEffect(() => { if (open) { setQuery(''); setIndex(0); } }, [open]);
  useEffect(() => { setIndex(0); }, [query]);

  if (!open) return null;

  const results = filterDestinations(destinations, query);
  const choose = (id: string) => { onSelect(id); onClose(); };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setIndex((i) => Math.min(i + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setIndex((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); const hit = results[index]; if (hit) choose(hit.id); }
  };

  return (
    <div className="cmdk-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div
        ref={ref}
        className="cmdk"
        role="dialog"
        aria-modal="true"
        aria-label={t('Cerca e vai a una sezione')}
        tabIndex={-1}
        onKeyDown={onKeyDown}
      >
        <div className="cmdk__search">
          <Icon name="search" size={16} />
          <input
            data-dialog-initial-focus
            className="cmdk__input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('Vai a…')}
            aria-label={t('Cerca sezione')}
          />
          <span className="kbd">Esc</span>
        </div>
        <div className="cmdk__list" role="listbox" aria-label={t('Sezioni')}>
          {results.length === 0 && <div className="cmdk__empty">{t('Nessun risultato')}</div>}
          {results.map((d, i) => (
            <button
              key={d.id}
              type="button"
              role="option"
              aria-selected={i === index}
              className={`cmdk__item ${i === index ? 'is-active' : ''}`}
              onMouseEnter={() => setIndex(i)}
              onClick={() => choose(d.id)}
            >
              <Icon name={d.icon} size={16} />
              <span className="cmdk__item-label">{t(d.label)}</span>
              <span className="cmdk__item-crumb">{t(d.crumb)}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
